import React, { useMemo, useState } from 'react';
import { normalizeReportPage } from '../core/modelGuards';
import ReportGrid from './ReportGrid';
import ReportWidgetEdit from './ReportWidgetEdit';
import { getBounds } from '../core/layout';

function toRectFromWidget(widget) {
  var bounds = getBounds(widget);
  return {
    x: bounds.x1,
    y: bounds.y1,
    width: bounds.width,
    height: bounds.height
  };
}

function intersectRect(a, b) {
  var aBounds = {
    x1: a.x,
    y1: a.y,
    x2: a.x + a.width,
    y2: a.y + a.height
  };
  var bBounds = {
    x1: b.x,
    y1: b.y,
    x2: b.x + b.width,
    y2: b.y + b.height
  };
  return aBounds.x1 < bBounds.x2 && aBounds.x2 > bBounds.x1 && aBounds.y1 < bBounds.y2 && aBounds.y2 > bBounds.y1;
}

function createRectFromCells(startCell, endCell) {
  var x1 = Math.min(startCell.x, endCell.x);
  var y1 = Math.min(startCell.y, endCell.y);
  var x2 = Math.max(startCell.x, endCell.x);
  var y2 = Math.max(startCell.y, endCell.y);

  return {
    x: x1,
    y: y1,
    width: x2 - x1 + 1,
    height: y2 - y1 + 1
  };
}

function createId(prefix) {
  return prefix + '-' + String(Date.now()) + '-' + String(Math.floor(Math.random() * 1000));
}

export default function ReportGridEditor({ initialPage, initialMode, editable, onPageChange }) {
  var [mode, setMode] = useState(initialMode === 'edit' ? 'edit' : 'report');
  var isEditable = editable !== false;
  var [page, setPage] = useState(function () {
    return normalizeReportPage(initialPage || {});
  });
  var [selectedWidgetId, setSelectedWidgetId] = useState(null);
  var [creationStartCell, setCreationStartCell] = useState(null);
  var [dragState, setDragState] = useState(null);

  var isInteractiveEditor = mode === 'edit' && isEditable;

  var selectedWidget = useMemo(function () {
    return page.widgets.find(function (widget) {
      return widget.id === selectedWidgetId;
    }) || null;
  }, [page.widgets, selectedWidgetId]);

  var updateWidget = function updateWidget(widgetId, patch) {
    if (!isInteractiveEditor) {
      return;
    }

    setPage(function (prevPage) {
      var next = {
        ...prevPage,
        widgets: prevPage.widgets.map(function (widget) {
          if (widget.id !== widgetId) {
            return widget;
          }

          return {
            ...widget,
            ...(patch || {})
          };
        })
      };

      if (onPageChange) {
        onPageChange(next);
      }

      return next;
    });
  };

  var validateRectAgainstWidgets = function validateRectAgainstWidgets(rect, widgets, ignoreWidgetId) {
    return !(widgets || []).some(function (widget) {
      if (widget.id === ignoreWidgetId) {
        return false;
      }
      return intersectRect(rect, toRectFromWidget(widget));
    });
  };

  var commitWidgetCreate = function commitWidgetCreate(newRect) {
    setPage(function (prevPage) {
      if (!validateRectAgainstWidgets(newRect, prevPage.widgets)) {
        setSelectedWidgetId(null);
        return prevPage;
      }

      var newWidget = {
        id: createId('widget'),
        type: 'single',
        xCoordinate: newRect.x,
        yCoordinate: newRect.y,
        width: newRect.width,
        height: newRect.height,
        data: {
          headerLabel: 'New Widget'
        }
      };

      var next = {
        ...prevPage,
        widgets: prevPage.widgets.concat(newWidget)
      };

      setSelectedWidgetId(newWidget.id);
      if (onPageChange) {
        onPageChange(next);
      }
      return next;
    });
  };

  var handleGridCellClick = function handleGridCellClick(cell) {
    if (!isInteractiveEditor) {
      return;
    }

    if (!creationStartCell) {
      setCreationStartCell(cell);
      return;
    }

    var newRect = createRectFromCells(creationStartCell, cell);
    setCreationStartCell(null);
    commitWidgetCreate(newRect);
  };

  var startDrag = function startDrag(widget, dragType, event) {
    if (!isInteractiveEditor) {
      return;
    }

    setSelectedWidgetId(widget.id);
    setDragState({
      widgetId: widget.id,
      dragType: dragType,
      startMouseX: event.clientX,
      startMouseY: event.clientY,
      originRect: toRectFromWidget(widget)
    });
  };

  var applyDrag = function applyDrag(event) {
    if (!dragState || !isInteractiveEditor) {
      return;
    }

    var deltaCols = Math.round((event.clientX - dragState.startMouseX) / 120);
    var deltaRows = Math.round((event.clientY - dragState.startMouseY) / 90);

    var candidate = {
      ...dragState.originRect
    };

    if (dragState.dragType === 'move') {
      candidate.x = Math.max(1, dragState.originRect.x + deltaCols);
      candidate.y = Math.max(1, dragState.originRect.y + deltaRows);
    } else {
      candidate.width = Math.max(1, dragState.originRect.width + deltaCols);
      candidate.height = Math.max(1, dragState.originRect.height + deltaRows);
    }

    setPage(function (prevPage) {
      if (!validateRectAgainstWidgets(candidate, prevPage.widgets, dragState.widgetId)) {
        return prevPage;
      }

      var next = {
        ...prevPage,
        widgets: prevPage.widgets.map(function (widget) {
          if (widget.id !== dragState.widgetId) {
            return widget;
          }
          return {
            ...widget,
            xCoordinate: candidate.x,
            yCoordinate: candidate.y,
            width: candidate.width,
            height: candidate.height
          };
        })
      };
      if (onPageChange) {
        onPageChange(next);
      }
      return next;
    });
  };

  var endDrag = function endDrag() {
    if (!dragState) {
      return;
    }
    setDragState(null);
  };

  React.useEffect(
    function bindDragEvents() {
      if (!dragState) {
        return function noop() {};
      }

      var handleMove = function handleMove(event) {
        applyDrag(event);
      };
      var handleUp = function handleUp() {
        endDrag();
      };

      window.addEventListener('mousemove', handleMove);
      window.addEventListener('mouseup', handleUp);

      return function cleanup() {
        window.removeEventListener('mousemove', handleMove);
        window.removeEventListener('mouseup', handleUp);
      };
    },
    [dragState]
  );

  return (
    <div className="re-editor-layout">
      <div className="re-editor-toolbar">
        <h2>{page.name}</h2>
        <div className="re-toolbar-actions">
          <button
            type="button"
            onClick={function onClick() {
              setMode(mode === 'edit' ? 'report' : 'edit');
              setCreationStartCell(null);
            }}
          >
            {mode === 'edit' ? 'Switch To Report Mode' : 'Switch To Edit Mode'}
          </button>
          {isInteractiveEditor ? <span className="re-editor-help-inline">Create Widget</span> : null}
        </div>
      </div>

      {isInteractiveEditor ? (
        <div className="re-editor-help">
          {creationStartCell
            ? 'Select the second corner to commit the rectangle.'
            : 'Click one cell to start, click another cell to create a widget.'}
        </div>
      ) : null}

      <div className="re-editor-content">
        <div className="re-editor-grid-wrap">
          <ReportGrid
            page={page}
            mode={mode}
            editable={isEditable}
            selectedWidgetId={selectedWidgetId}
            onSelectWidget={function onSelectWidget(widgetId) {
              if (isInteractiveEditor) {
                setSelectedWidgetId(widgetId);
              }
            }}
            onGridCellClick={handleGridCellClick}
            onWidgetMoveStart={function onWidgetMoveStart(widget, event) {
              startDrag(widget, 'move', event);
            }}
            onWidgetResizeStart={function onWidgetResizeStart(widget, event) {
              startDrag(widget, 'resize', event);
            }}
          />
        </div>

        {isInteractiveEditor ? (
          <ReportWidgetEdit selectedWidget={selectedWidget} onWidgetChange={updateWidget} />
        ) : null}
      </div>
    </div>
  );
}
